import {
  estimateWait,
  formatWaitEstimate,
  queuedCount,
  runningCount,
} from "@/shared/domain/actions";
import { useInvocationQueueStats } from "@/shared/domain/actions/hooks";

import styles from "./QueueDepthWidget.module.css";

/**
 * Both halves of an action's identity, or neither: the route rejects a half-supplied pair, and
 * `ActionService.getInvocationQueueStats` drops both unless both are given, so a caller with only
 * one would silently get org-wide figures.
 *
 * Supplying them narrows `median_duration_seconds` to that action. The depth and
 * `queued_work_seconds` stay org-wide, and since `estimateWait` prefers
 * `queued_work_seconds` whenever the server sends it, the action's median reaches the estimate
 * only as the in-flight term. It prices the queue itself only against a server old enough to omit
 * `queued_work_seconds`.
 */
export type QueueDepthWidgetProps =
  | { actionName: string; actionOrgId: string }
  | { actionName?: undefined; actionOrgId?: undefined };

/**
 * How many invocations are waiting in the org's queue, and roughly how long they'll take to clear.
 * Renders nothing when nothing is queued.
 *
 * Shown on the invoke form, so a submitter sees what their invocation will land behind before
 * submitting it. The counts are the org's whole queue, not this action's.
 *
 * The status words are the ones `invocationStatusToString` gives the Invocations page — "pending"
 * for `Queued`, and "provisioning or running" for the compute-holding statuses `runningCount`
 * folds together — so a reader can carry this line straight to that page's status filter.
 */
export function QueueDepthWidget({
  actionName,
  actionOrgId,
}: QueueDepthWidgetProps) {
  const { data: stats } = useInvocationQueueStats({
    actionName,
    actionOrgId,
    // Stated rather than inherited. The hook's 10s default is for a page someone is
    // watching; this sits on the invoke form for as long as it takes to fill the form
    // in, and every poll runs three aggregates over `invocations`, two of them
    // `percentile_cont` CTEs. A queue depth a submitter glances at before clicking
    // does not need re-costing that often.
    refetchInterval: 30_000,
  });

  if (stats === undefined) {
    return null;
  }

  const queued = queuedCount(stats);

  if (queued === 0) {
    return null;
  }

  const running = runningCount(stats);

  const wait = estimateWait(stats);

  return (
    <div className={styles.widget} data-testid="queue-depth-widget">
      <span className={styles.depth}>
        {queued.toLocaleString()} {queued === 1 ? "invocation" : "invocations"}{" "}
        pending
      </span>
      {running > 0 && (
        <span className={styles.running}>
          ({running.toLocaleString()} provisioning or running now)
        </span>
      )}
      {wait.kind === "estimated" && (
        <span className={styles.estimate}>
          · estimated wait {formatWaitEstimate(wait.seconds)}
        </span>
      )}
    </div>
  );
}
